import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type ConfirmOptions = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  /** Red-button styling for destructive actions. */
  danger?: boolean;
  /** If true, hide the cancel button (single-OK dialog, like alert()). */
  alertMode?: boolean;
};

type Resolver = (ok: boolean) => void;

type State = (ConfirmOptions & { resolve: Resolver }) | null;

const ConfirmCtx = createContext<(opts: ConfirmOptions) => Promise<boolean>>(() => Promise.resolve(false));

/** Global confirm/alert dialog provider. Wrap the app once; use
 *  useConfirm()/useAlert() anywhere to pop a modal and await a boolean. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const close = (ok: boolean) => {
    if (!state) return;
    state.resolve(ok);
    setState(null);
  };

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') close(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
            onClick={(e) => {
              e.stopPropagation();
              if (e.target === e.currentTarget) close(false);
            }}
          >
            <Card
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
            >
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  {state.danger && (
                    <div className="shrink-0 w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{state.title}</h3>
                    {state.description && (
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {state.description}
                      </p>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 -mr-2 -mt-1" onClick={() => close(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex justify-end gap-2">
                  {!state.alertMode && (
                    <Button variant="outline" onClick={() => close(false)}>
                      {state.cancelText ?? 'İptal'}
                    </Button>
                  )}
                  <Button
                    className={cn(
                      state.danger && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                    )}
                    onClick={() => close(true)}
                    autoFocus
                  >
                    {state.confirmText ?? (state.alertMode ? 'Tamam' : 'Onayla')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>,
          document.body,
        )}
    </ConfirmCtx.Provider>
  );
}

/** Await a confirmation — true if the user clicked confirm, false otherwise. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  return useContext(ConfirmCtx);
}

/** Convenience: single-button alert modal. Always resolves true when
 *  dismissed — use it just to show a message. */
export function useAlert(): (opts: Omit<ConfirmOptions, 'alertMode'>) => Promise<void> {
  const confirm = useContext(ConfirmCtx);
  return useCallback(
    async (opts) => {
      await confirm({ ...opts, alertMode: true });
    },
    [confirm],
  );
}
